import type {
  CombinePortfoliosOrganizeOptionEnum,
  OperationError,
  OperationResult,
  LibrarySearchEntity,
  Portfolio,
} from 'venn-api';
import { getFund, getPrivateFund, getPrivatePortfolio, getSpecificPortfolioV3 } from 'venn-api';
import { atomFamily, selectorFamily, waitForAll } from 'recoil';
import { isNil } from 'lodash';
import {
  AnalysisSubject,
  type AnalysisSubjectType,
  assertExhaustive,
  assertNotNil,
  combinePortfolios,
  StudioErrorsEnum,
} from 'venn-utils';
import {
  incrementOnStudioReset,
  incrementOnSubjectChange,
  incrementOnWorkspaceConfigurationChange,
} from '../../effects/signalEffects';
import type { Subject, SubjectWithOptionalFeeAndFilters } from '../types';
import { toSubjectOnly } from '../types';
import { aggregatedGroupingState, allocatorAnalysisSubject } from './allocator';
import { proxySetIdState } from './view';

/**
 * An id that is a dependency of apiAnalysisSubjectQuery
 * so that its cache will be cleared when this atom changes
 * See https://recoiljs.org/docs/guides/asynchronous-data-queries/#use-a-request-id for more info
 */
const apiAnalysisSubjectQueryId = atomFamily<number, Subject>({
  key: 'apiAnalysisSubjectQueryId',
  default: 0,
  effects: (subject) => [
    incrementOnStudioReset,
    incrementOnSubjectChange(subject),
    incrementOnWorkspaceConfigurationChange,
  ],
});

/**
 * A Live Aggregated Portfolio persists only its aggregatedPortfolioSettings on the root node with no children;
 * combined tree is meant to be rebuilt from those settings against the current state of the underlying
 * portfolios on every read. Backend does not rebuild it yet, so the frontend does it here. Once backend
 * returns combined tree on read, this helper and its call site can be removed.
 */
export const rebuildAggregatedPortfolio = async (
  portfolio: Portfolio | undefined,
  proxySetId: string | undefined,
  organizeOverride: CombinePortfoliosOrganizeOptionEnum | undefined,
): Promise<Portfolio | undefined> => {
  const settings = portfolio?.aggregatedPortfolioSettings;
  if (!portfolio || !settings) {
    return portfolio;
  }
  const { portfolioInformation, combineSettings } = settings;
  const constituents = await Promise.all(
    portfolioInformation.map((info) =>
      getSpecificPortfolioV3(info.id, undefined, proxySetId).then((res) => res.content),
    ),
  );
  const combined = combinePortfolios(constituents, portfolioInformation, {
    ...combineSettings,
    organizeOption: organizeOverride ?? combineSettings.organizeOption,
  })[0];
  if (!combined) {
    return portfolio;
  }
  // combinePortfolios sums the constituents' allocation onto the combined root but not netAssets (mergePortfolios
  // ignores it); the allocator reads netAssets instead of allocation under the leverage/short FF, so sum it here.
  return {
    ...portfolio,
    children: combined.children,
    ...(isNil(combined.allocation) ? {} : { allocation: combined.allocation }),
    ...(constituents.some((constituent) => !isNil(constituent?.netAssets))
      ? { netAssets: constituents.reduce((total, constituent) => total + (constituent?.netAssets ?? 0), 0) }
      : {}),
  };
};

const fetchSubjectItem = async (
  subject: Subject,
  proxySetId: string | undefined,
  organizeOverride: CombinePortfoliosOrganizeOptionEnum | undefined,
) => {
  switch (subject.type) {
    case 'portfolio': {
      if (Number.isNaN(subject.portfolioId)) {
        // Defensive: a portfolio subject must carry a real id; a NaN id means an unresolved/degraded subject.
        throw new Error(StudioErrorsEnum.SUBJECT_FAILED_TO_LOAD);
      }
      const portfolio = (await getSpecificPortfolioV3(subject.portfolioId, subject.portfolioVersion, proxySetId))
        .content;
      return rebuildAggregatedPortfolio(portfolio, proxySetId, organizeOverride);
    }
    case 'fund':
      return (await getFund(subject.fundId, proxySetId)).content;
    case 'private-portfolio':
      return (await getPrivatePortfolio(subject.privatePortfolioId)).content;
    case 'private-fund':
      return (await getPrivateFund(subject.privateFundId)).content;
    default:
      return assertExhaustive(subject);
  }
};

const apiAnalysisSubjectQuery = selectorFamily<AnalysisSubject | undefined, Subject>({
  key: 'apiAnalysisSubjectQuery',
  get:
    (subject) =>
    async ({ get }) => {
      try {
        get(apiAnalysisSubjectQueryId(subject));

        const proxySetId = get(proxySetIdState);
        const organizeOverride =
          subject.type === 'portfolio' ? get(aggregatedGroupingState(subject.portfolioId)) : undefined;

        const item = await fetchSubjectItem(subject, proxySetId, organizeOverride);
        if (!item) {
          return undefined;
        }

        return new AnalysisSubject(item, getLibrarySearchEntityAnalysisSubjectType(subject), proxySetId, {
          isAggregatedPortfolio: 'aggregatedPortfolioSettings' in item && !isNil(item.aggregatedPortfolioSettings),
        });
      } catch (error) {
        const { status } = error as OperationResult<OperationError>;
        if (status === 403) {
          throw new Error(StudioErrorsEnum.SUBJECT_NO_PERMISSION);
        } else {
          throw new Error(StudioErrorsEnum.SUBJECT_FAILED_TO_LOAD);
        }
      }
    },
});

/** Subject query excluding any impact of allocator panel. */
export const originalAnalysisSubjectQuery = selectorFamily<AnalysisSubject | undefined, Subject | undefined>({
  key: 'originalAnalysisSubjectQuery',
  get:
    (subject) =>
    ({ get }) => {
      const subjectOnly = toSubjectOnly(subject);
      if (!subjectOnly) {
        return undefined;
      }
      return get(apiAnalysisSubjectQuery(subjectOnly));
    },
});

/** Single subject query, including any impact from allocator panel. */
export const analysisSubjectQuery = selectorFamily<AnalysisSubject, SubjectWithOptionalFeeAndFilters>({
  key: 'analysisSubject',
  get:
    (subject) =>
    ({ get }) => {
      const subjectToQuery: Subject | undefined = toSubjectOnly(subject);
      return assertNotNil(
        get(allocatorAnalysisSubject(subjectToQuery)) ?? get(originalAnalysisSubjectQuery(subjectToQuery)),
      );
    },
});

/** Uses {@link analysisSubjectQuery} to query for multiple subjects. */
export const analysisSubjects = selectorFamily<AnalysisSubject[], Subject[]>({
  key: 'analysisSubjects',
  get: (subjects) => () => waitForAll(subjects.map((subject) => analysisSubjectQuery(subject))),
});

export const getLibrarySearchEntityAnalysisSubjectType = (
  entity: LibrarySearchEntity | Subject,
): AnalysisSubjectType => {
  if ('type' in entity) {
    switch (entity.type) {
      case 'fund':
        return 'investment';
      case 'private-fund':
        return 'private-investment';
      case 'portfolio':
        return 'portfolio';
      case 'private-portfolio':
        return 'private-portfolio';
      default:
        return assertExhaustive(entity);
    }
  }
  return entity.privateFundId
    ? 'private-investment'
    : entity.privatePortfolioId
      ? 'private-portfolio'
      : entity.fundId
        ? 'investment'
        : 'portfolio';
};
